import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Formik, Form } from "formik";
import { gql, useMutation, useQuery } from "urql";
import { sortBy } from "lodash";
import { useParams, useLocation } from "wouter";
import { Alert, AlertVariant } from "../../../../../components/src/Alert";

import { usePrevious, useAlertTimeOut } from "../../../../../components/src/common/Utils";
import { SubmitButton, TextField, SelectField, DateField } from "../../../../../components/src/form";

import { CostFilterInput, CurrencyEnum, Instrument, InstrumentProductTypeEnum, OpPaymentInput } from "../../../types.generated";
import { OP_PAYMENTS_QUERY } from "../../../../../graphql/src/oppayment/queries";
import { TooltipLabel } from "../../../../../components/src/TooltipLabel";

// TODO: Add creditor name, iban and bic options. Currently we do not have for example
// danske bank/ morgan stanley bank accounts saved on these parties. Needs to be remodeled.
export const GetParties = gql`
    query GetParties {
        parties(filter: { typeIn: [Client], statusIn: [Confirmed] }) {
            _id
            name
            types
            instruments {
                _id
                name
                bic
                iban
                productType
            }
        }
    }
`;

const CREATE_OP_PAYMENTS = gql`
    mutation opCreatePayments($payments: [OPPaymentInput!]!) {
        opCreatePayments(payments: $payments) {
            _id
        }
    }
`;

type PaymentInputFlat = {
    instructedAmount: string;
    currency: CurrencyEnum;
    debtorAccountIban: string;
    creditorName: string;
    creditorAccountIban: string;
    creditorAgentBic: string;
    remittanceInformationUnstructured: string;
    debtorAccountMessage: string;
    requestedExecutionDate: string;
    endToEndIdentification: string;
    paymentId: string;
};

const getDefaultDocument = (today: string): PaymentInputFlat => {
    const payment: PaymentInputFlat = {
        instructedAmount: null,
        currency: CurrencyEnum.SEK,
        debtorAccountIban: "",
        creditorName: "",
        creditorAccountIban: "",
        creditorAgentBic: "",
        remittanceInformationUnstructured: "",
        debtorAccountMessage: "",
        requestedExecutionDate: today,
        endToEndIdentification: "",
        paymentId: ""
    };

    return payment;
};

export function PaymentForm(): React.ReactElement {
    const { id } = useParams<{ id: string }>();
    const [, navigate] = useLocation();
    const previousId: string = usePrevious(id);
    const isEditMode = id === "new" ? false : true;
    const isCreateMode = !isEditMode;

    const today = new Date().toISOString().slice(0, 10);

    const filter: CostFilterInput = { idIn: [id] };

    const [{ fetching: loading, error, data }] = useQuery({
        query: OP_PAYMENTS_QUERY,
        variables: { filter },
        pause: isCreateMode,
        requestPolicy: "cache-and-network"
    });

    const [__, opCreatePayments] = useMutation(CREATE_OP_PAYMENTS);

    const [{ fetching: loadingParties, error: errorParties, data: dataParties }] = useQuery({
        query: GetParties,
        requestPolicy: "cache-and-network"
    });
    const [payment, setPayment] = useState<PaymentInputFlat>(null);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({ color: "info", visible: false, message: "" });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    useEffect(() => {
        if (id === "new" && !payment) {
            setPayment(getDefaultDocument(today));
        } else if (previousId && id !== previousId) {
            setPayment(null);
        } else if (data && data.opPayments && !payment) {
            console.log(data.opPayments);
            const paymentFormatted: PaymentInputFlat = {
                instructedAmount: data.opPayments[0].instructedAmount ? data.opPayments[0].instructedAmount.amount : null,
                currency: data.opPayments[0].instructedAmount ? data.opPayments[0].instructedAmount.currency : null,
                debtorAccountIban: data.opPayments[0].debtorAccount ? data.opPayments[0].debtorAccount.iban : null,
                creditorName: data.opPayments[0].creditorName,
                creditorAccountIban: data.opPayments[0].creditorAccount ? data.opPayments[0].creditorAccount.iban : null,
                creditorAgentBic: data.opPayments[0].creditorAgent ? data.opPayments[0].creditorAgent : null,
                remittanceInformationUnstructured: data.opPayments[0].remittanceInformationUnstructured,
                debtorAccountMessage: data.opPayments[0].debtorAccountMessage,
                requestedExecutionDate: data.opPayments[0].requestedExecutionDate ? data.opPayments[0].requestedExecutionDate : null,
                endToEndIdentification: data.opPayments[0].endToEndIdentification ? data.opPayments[0].endToEndIdentification : null,
                paymentId: data.opPayments[0].paymentId ? data.opPayments[0].paymentId : ""
            };
            setPayment(paymentFormatted);
        } else if (!payment) {
            setPayment(null);
        }
    }, [payment, data, id, previousId, today]);

    const { debtorIbanOptions, instrumentsByIban } = useMemo<{
        debtorIbanOptions: { key: string; value: string }[];
        instrumentsByIban: Record<string, Instrument>;
    }>(() => {
        if (!dataParties) {
            return {
                debtorIbanOptions: [],
                instrumentsByIban: {}
            };
        }

        const ibans: Record<string, { key: string; value: string }> = {};
        const instruments: Record<string, Instrument> = {};

        for (const party of dataParties.parties) {
            for (const instrument of party.instruments) {
                if (instrument.bic && instrument.productType === InstrumentProductTypeEnum.CashAccount) {
                    instruments[instrument.iban] = instrument;

                    const instrumentKey = `${instrument.bic}:${party._id}:${instrument.iban}`;

                    ibans[instrumentKey] = {
                        key: instrument.iban,
                        value: `${party.name}-${instrument.name}-${instrument.iban}`
                    };
                }
            }
        }

        return {
            debtorIbanOptions: Object.values(ibans),
            instrumentsByIban: instruments
        };
    }, [dataParties]);

    if (loading || loadingParties) return <div>Loading</div>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (errorParties) return <p>error: {JSON.stringify(errorParties, null, 2)}</p>;
    if (!payment) return <div>No payment found</div>;

    return (
        <div id="paymentform" className="paymentform form">
            <Formik
                enableReinitialize={true}
                initialValues={payment}
                validate={(validateValues) => {
                    const errors: any = {};
                    const requiredFields: string[] = [
                        "instructedAmount",
                        "currency",
                        "creditorName",
                        "debtorAccountIban",
                        "creditorAccountIban",
                        "creditorAgentBic",
                        "remittanceInformationUnstructured"
                    ];

                    requiredFields.forEach((d) => {
                        if (!validateValues[d] || validateValues[d] === "None") {
                            errors[d] = "Required";
                        }
                    });

                    return Object.keys(errors).length > 0 ? errors : {};
                }}
                onSubmit={async (submitValues, { setSubmitting }) => {
                    const input: OpPaymentInput = {
                        instructedAmount: {
                            amount: submitValues.instructedAmount,
                            currency: submitValues.currency
                        },
                        debtorAccount: {
                            iban: submitValues.debtorAccountIban
                        },
                        creditorName: submitValues.creditorName,
                        creditorAccount: {
                            iban: submitValues.creditorAccountIban
                        },
                        creditorAgent: submitValues.creditorAgentBic,
                        remittanceInformationUnstructured: submitValues.remittanceInformationUnstructured
                    };

                    if (submitValues.endToEndIdentification) {
                        input.endToEndIdentification = submitValues.endToEndIdentification;
                    }

                    if (submitValues.debtorAccountMessage) {
                        input.debtorAccountMessage = submitValues.debtorAccountMessage;
                    }

                    // Must be a future date, not including in input if today
                    if (submitValues.requestedExecutionDate !== today) {
                        input.requestedExecutionDate = submitValues.requestedExecutionDate;
                    }

                    await opCreatePayments({ payments: [input] })
                        .then((result) => {
                            if (result.error) {
                                setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                return;
                            }
                            console.log(result.data);
                            const payment = result.data.opCreatePayments[0];
                            if (isCreateMode) {
                                navigate("/openpayments/payments/" + payment._id, { replace: true });
                            } else {
                                const paymentFormatted: PaymentInputFlat = {
                                    instructedAmount: payment.instructedAmount.amount,
                                    currency: instrumentsByIban[payment.debtorAccount.iban].currency,
                                    debtorAccountIban: payment.debtorAccount.iban,
                                    creditorName: payment.creditorName,
                                    creditorAccountIban: payment.creditorAccount.iban,
                                    creditorAgentBic: payment.creditorAgent,
                                    remittanceInformationUnstructured: payment.remittanceInformationUnstructured,
                                    debtorAccountMessage: payment.debtorAccountMessage,
                                    requestedExecutionDate: payment.requestedExecutionDate,
                                    endToEndIdentification: payment.endToEndIdentification,
                                    paymentId: submitValues.paymentId
                                };
                                setPayment(paymentFormatted);
                                setAlert({
                                    color: "success",
                                    visible: true,
                                    message: `Payment '${payment._id}' created successfully!`
                                });
                            }
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, errors }) => {
                    if (Object.keys(errors).length > 0) {
                        console.log(errors);
                    }
                    return (
                        <Fragment>
                            <div className="flex flex-wrap -mx-2">
                                <Form autoComplete="off">
                                    {isEditMode ? (
                                        <div className="w-full px-2 mb-4">
                                            <TextField name="paymentId" label="Payment id" className="" disabled={false} readOnly={true} />
                                        </div>
                                    ) : null}
                                    <div className="w-full px-2 mb-4">
                                        <SelectField
                                            name="debtorAccountIban"
                                            label="Debtor account IBAN"
                                            options={sortBy(debtorIbanOptions, "value")}
                                            className=""
                                            disabled={isSubmitting || (id && id !== "new")}
                                        />
                                    </div>
                                    <div className="w-full px-2 mb-4">
                                        <TextField
                                            name="debtorAccountMessage"
                                            label="Debtor account message"
                                            className=""
                                            disabled={isSubmitting || (id && id !== "new")}
                                        />
                                    </div>
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full md:w-6/12 px-2 mb-4">
                                            <DateField
                                                name="requestedExecutionDate"
                                                label="Requested execution date"
                                                className=""
                                                disabled={isSubmitting || (id && id !== "new")}
                                            />
                                        </div>
                                        <div className="w-full md:w-6/12 px-2 mb-4 mt-3">
                                            <TextField
                                                name="instructedAmount"
                                                label="Amount"
                                                className=""
                                                disabled={isSubmitting || (id && id !== "new")}
                                            />
                                        </div>
                                    </div>
                                    <div className="w-full px-2 mb-4">
                                        <TextField
                                            name="creditorName"
                                            label={
                                                <TooltipLabel
                                                    text={"Creditor name"}
                                                    content={
                                                        "Should be on format Creditor Name Example. Max 35 characters, should match actual name in bank."
                                                    }
                                                />
                                            }
                                            className=""
                                            disabled={isSubmitting || (id && id !== "new")}
                                        />
                                    </div>
                                    <div className="w-full px-2 mb-4">
                                        <TextField
                                            name="creditorAccountIban"
                                            label="Creditor account IBAN"
                                            className=""
                                            disabled={isSubmitting || (id && id !== "new")}
                                        />
                                    </div>
                                    <div className="w-full px-2 mb-4">
                                        <TextField
                                            name="creditorAgentBic"
                                            label="Creditor agent BIC"
                                            className=""
                                            disabled={isSubmitting || (id && id !== "new")}
                                        />
                                    </div>
                                    <div className="w-full px-2 mb-4">
                                        <TextField
                                            name="endToEndIdentification"
                                            label="End to end identification"
                                            className=""
                                            disabled={isSubmitting || (id && id !== "new")}
                                        />
                                    </div>
                                    <div className="w-full px-2 mb-4">
                                        <TextField
                                            name="remittanceInformationUnstructured"
                                            label="Reference"
                                            className=""
                                            disabled={isSubmitting || (id && id !== "new")}
                                        />
                                    </div>
                                    <div className="w-full px-2 mb-4">
                                        <TextField name="_id" label="Id" type="text" className="" disabled={true} />
                                    </div>
                                    <SubmitButton
                                        className="btn btn-primary mb-2"
                                        disabled={isSubmitting || Object.keys(errors).length > 0 || (id && id !== "new")}
                                        label={id === "new" ? "Create" : "Update"}
                                    />

                                    {alert.visible ? (
                                        <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                                            {alert.message}
                                        </Alert>
                                    ) : null}
                                </Form>
                            </div>
                        </Fragment>
                    );
                }}
            </Formik>
        </div>
    );
}
